import type { MaquinariaDeInforme } from "@/lib/informes";
import type {
  CampoDeTerreno,
  ItemDeTerreno,
  RegistroDeTerrenoDelInforme,
  SeccionDeTerreno,
} from "@/lib/terreno";

import { MAQUINA_VACIA, type FormularioDeMaquina } from "./maquinaria";
import type { ModoDelTerreno } from "./cuadroDeConstrucciones";

/* ---------------------------------------------------------------------------
   Lo que la pestaña "Maquinarias y equipos" lee del registro de terreno: las
   máquinas que el encargado creó en el predio, los registros que cuelgan de
   cada fila y si una máquina de la web "no está en el predio".

   Es lógica PURA, gemela de `aguasTerreno.ts`, y vive fuera del componente por
   el mismo motivo: decidir a qué fila pertenece un registro, o si una máquina
   de terreno se ofrece para adoptar, son reglas que pueden estar mal sin que
   nada se vea roto.

   LA DIFERENCIA CON AGUAS: las máquinas se guardan de a UNA (POST/PATCH), no con
   un PUT de la pestaña. Por eso acá no hay "borradores" en el estado del
   formulario: una máquina de terreno se ADOPTA abriendo el formulario de alta
   precargado, y "Es la misma que" es un PATCH de la fila elegida.
--------------------------------------------------------------------------- */

const PREFIJO_DE_TERRENO = "maquinaria-terreno:";

/** Una máquina que el encargado creó en la App y que ninguna fila adoptó todavía. */
export interface ItemDeMaquinaPorEnlazar {
  uuid: string;
  item: ItemDeTerreno;
}

/** Todo lo que la lista de máquinas necesita saber del terreno. */
export interface TerrenoDeLaLista {
  informeId: number;
  modo: ModoDelTerreno;
  /** Quien fue a terreno: sus iniciales encabezan el registro. */
  encargado: string | null;
  items: ItemDeTerreno[];
  porEnlazar: ItemDeMaquinaPorEnlazar[];
  onAbrirFoto: (uuid: string) => void;
  /** "Es la misma que": enlaza el registro de terreno a una fila ya guardada. */
  onVincular: (uuid: string, maquinaId: number) => void;
  /** «Quitar de la lista» del aviso de ausencia. */
  onQuitar?: (filaId: number) => void;
  /** «Mantener» del aviso de ausencia. */
  onMantener?: (filaId: number) => void;
}

/** La sección de maquinaria del registro de terreno, o `null`. */
export function seccionDeMaquinarias(
  registro: RegistroDeTerrenoDelInforme | undefined,
): SeccionDeTerreno | null {
  return registro?.secciones.find((s) => s.clave === "maquinarias") ?? null;
}

/**
 * Las máquinas de terreno que todavía no tienen fila en el cuadro (el servidor
 * cuelga las ya enlazadas de `maquinaria:<id>` y deja de listarlas aparte).
 *
 * TAMBIÉN SE DESCARTA LO QUE UNA FILA GUARDADA YA ENLAZA: entre el alta con
 * `terreno_ref` y la relectura del registro hay un instante en que el informe
 * ya tiene la fila y el registro todavía lista la máquina suelta, y se vería
 * dos veces.
 */
export function itemsPorEnlazarDeMaquinarias(
  registro: RegistroDeTerrenoDelInforme | undefined,
  guardadas: Pick<MaquinariaDeInforme, "terreno_ref">[] = [],
): ItemDeMaquinaPorEnlazar[] {
  const enlazadas = new Set(guardadas.flatMap((m) => (m.terreno_ref ? [m.terreno_ref] : [])));

  return (seccionDeMaquinarias(registro)?.items ?? []).flatMap((item) => {
    if (!item.nuevo || !item.referencia.startsWith(PREFIJO_DE_TERRENO)) return [];

    const uuid = item.referencia.slice(PREFIJO_DE_TERRENO.length);

    return enlazadas.has(uuid) ? [] : [{ uuid, item }];
  });
}

/** "2 máquinas o equipos creados en terreno": el título del aviso, concordado. */
export function tituloDelAviso(cuantas: number): string {
  return cuantas === 1
    ? "1 máquina o equipo creado en terreno"
    : `${cuantas} máquinas o equipos creados en terreno`;
}

/** La clave de la fila de una máquina de terreno (expansión del registro). */
export function claveDeTerreno(uuid: string): string {
  return `terreno-${uuid}`;
}

/** La clave de la fila de una máquina guardada. */
export function claveDeMaquina(id: number): string {
  return `maquina-${id}`;
}

/**
 * El formulario de alta precargado con lo que la App ya sabe: nombre, año y
 * unidades. La valorización queda en blanco — es lo que se completa en la web.
 */
export function formularioDesdeElItem(item: ItemDeTerreno): FormularioDeMaquina {
  return {
    ...MAQUINA_VACIA,
    nombre: item.nombre,
    anio_fabricacion: item.anio_fabricacion == null ? "" : String(item.anio_fabricacion),
    unidades: item.unidades == null ? MAQUINA_VACIA.unidades : String(item.unidades),
  };
}

/**
 * El ítem de terreno cuyos registros se muestran bajo una fila guardada: el
 * servidor ya colgó ahí lo enlazado (`maquinaria:<id>`).
 */
export function itemDeLaMaquina(
  items: ItemDeTerreno[],
  maquina: Pick<MaquinariaDeInforme, "id">,
): ItemDeTerreno | null {
  return items.find((i) => i.referencia === `maquinaria:${maquina.id}`) ?? null;
}

/** "No está en el predio": una observación del encargado, nunca un dato de la fila. */
export function ausenteEnTerreno(item: ItemDeTerreno | null): boolean {
  return item?.ausente === true;
}

/**
 * ¿La fila nació en la App de terreno y todavía no tiene datos de valorización?
 *
 * `null` es una fila TERRENO que nadie guardó (por definición no tiene nada que
 * valorizar). Una fila guardada cuenta cuando está enlazada (`terreno_ref`) y NO
 * tiene ninguno de los dos valores unitarios: con uno solo ya empezó a
 * valorizarse. Una fila hecha a mano no es «creada en la App».
 */
export function creadaEnTerrenoSinValorizar(
  fila: Pick<MaquinariaDeInforme, "terreno_ref" | "valor_unitario_reposicion" | "valor_unitario_residual"> | null,
): boolean {
  if (fila === null) return true;

  return (
    fila.terreno_ref !== null &&
    fila.valor_unitario_reposicion === null &&
    fila.valor_unitario_residual === null
  );
}

/**
 * ¿Hay que mostrar el aviso «Según terreno, no está en el predio»?
 *
 * Sí cuando el encargado dio la fila por ausente y nadie ha decidido todavía, o
 * cuando decidieron «Mantener» y el encargado volvió a observar la ausencia
 * DESPUÉS. Se compara el momento de la observación (`ausente_desde`) con el del
 * «Mantener»: sin el primero no se puede probar que sea más nueva, así que se
 * respeta la decisión ya tomada. Un «Mantener» ilegible se trata como no
 * decidido: preguntar de más es la falla barata.
 */
export function debeAvisarLaAusencia(
  item: ItemDeTerreno | null,
  mantenidaEn: string | null | undefined,
): boolean {
  if (!ausenteEnTerreno(item)) return false;
  if (!mantenidaEn) return true;

  const mantenida = Date.parse(mantenidaEn);
  if (Number.isNaN(mantenida)) return true;

  const observada = item?.ausente_desde ? Date.parse(item.ausente_desde) : Number.NaN;
  if (Number.isNaN(observada)) return false;

  return observada > mantenida;
}

export interface OpcionParaVincular {
  clave: string;
  etiqueta: string;
}

/**
 * Las filas con las que una máquina de terreno puede decir "es la misma que":
 * las ya guardadas que todavía no tienen un registro enlazado (el servidor
 * exige que cada uuid se use una sola vez por informe).
 */
export function opcionesParaVincular(
  maquinas: Pick<MaquinariaDeInforme, "id" | "nombre" | "terreno_ref">[],
): OpcionParaVincular[] {
  return maquinas
    .filter((m) => m.terreno_ref === null)
    .map((m) => ({ clave: String(m.id), etiqueta: m.nombre }));
}

/** Lo que el encargado dijo de la descripción general de la maquinaria, o `null`. */
export function campoDeLaDescripcionDeMaquinarias(
  registro: RegistroDeTerrenoDelInforme | undefined,
): CampoDeTerreno | null {
  return registro?.secciones.find((s) => s.clave === "maquinarias")?.campos.find((c) => c.clave === "maquinarias") ?? null;
}
